import axios from "axios";
import { API_BASE, API_ORIGIN } from "@/lib/apiConfig";
import { captureError } from "@/lib/sentry";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const getStoredCsrfToken = () => sessionStorage.getItem("csrf_token");

const storeCsrfToken = (csrfToken) => {
  if (csrfToken) {
    sessionStorage.setItem("csrf_token", csrfToken);
  }
  return csrfToken;
};

const refreshCsrfToken = async () => {
  const response = await axios.get(`${API_BASE}/auth/csrf`, { withCredentials: true });
  return storeCsrfToken(response.data?.csrf_token);
};

// Add CSRF token for cookie-authenticated browser writes. The JWT itself is
// stored in an HttpOnly cookie and is never read by frontend JavaScript.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrfToken = getStoredCsrfToken();
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const isCsrfRejection = (
      status === 403
      && typeof error.response?.data?.detail === "string"
      && error.response.data.detail.toLowerCase().includes("csrf")
    );
    if (isCsrfRejection && error.config && !error.config._csrfRetry) {
      try {
        const csrfToken = await refreshCsrfToken();
        if (csrfToken) {
          error.config._csrfRetry = true;
          error.config.headers = {
            ...(error.config.headers || {}),
            "X-CSRF-Token": csrfToken,
          };
          return api(error.config);
        }
      } catch (refreshError) {
        captureError(refreshError, {
          type: "csrf_refresh_failed",
          method: error.config?.method,
          url: error.config?.url,
          originalStatus: status,
        });
      }
    }
    if (!status || status >= 500) {
      captureError(error, {
        type: "api_error",
        method: error.config?.method,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        status,
        response: error.response?.data,
      });
    } else if (isCsrfRejection) {
      captureError(error, {
        type: "csrf_rejected",
        method: error.config?.method,
        url: error.config?.url,
        hasCsrfToken: Boolean(getStoredCsrfToken()),
      });
    }
    return Promise.reject(error);
  },
);

// ==================== CONTENT API ====================
// Clean REST interface for content items

export const contentApi = {
  // Get nearby content items based on location
  getNearby: (lat, lng, options = {}) => {
    const params = new URLSearchParams();
    params.append('lat', lat);
    params.append('lng', lng);
    if (options.radius) params.append('radius', options.radius);
    if (options.limit) params.append('limit', options.limit);
    if (options.category) params.append('category', options.category);
    return api.get(`/content/nearby?${params.toString()}`);
  },
  
  // Get a single content item by ID
  getById: (contentId, lang = "en") => api.get(`/content/${contentId}`, { params: { lang } }),
  
  // List all content items with optional filtering
  list: (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.category) params.append('category', options.category);
    if (options.hasAudio !== undefined) params.append('has_audio', options.hasAudio);
    return api.get(`/content?${params.toString()}`);
  },
  
  // Generate summary + audio for a content item (uses 1 credit)
  generateSummary: (contentId, lang = "en", forceCharge = false) => api.post(`/content/${contentId}/generate-summary?lang=${lang}${forceCharge ? '&force_charge=true' : ''}`),
  
  // Get related/nearby places for a content item
  getRelated: (contentId, limit = 5) => api.get(`/content/${contentId}/related?limit=${limit}`),
  
  // List supported languages
  getLanguages: () => api.get("/content/languages"),

  // Search autocomplete
  searchAutocomplete: (query, lat, lng, options = {}) => {
    const params = { query };
    if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
    if (options.types) params.types = options.types;
    return api.get("/content/search-autocomplete", { params });
  },

  // Get place details by Google placeId
  getPlaceDetails: (placeId) => api.get("/content/place-details", { params: { place_id: placeId } }),

  // Ensure a marker exists for a Google Place (creates if needed)
  ensureMarkerFromPlace: (placeId) => api.post("/content/markers/from-place", null, { params: { place_id: placeId } }),

  // Generation status polling
  getGenerationStatus: (jobId) => api.get(`/content/generation-status/${jobId}`),
  getActiveGeneration: (contentId, lang = "en") => api.get(`/content/${contentId}/generation-active?lang=${lang}`),
  
  // Get streaming audio URL (not an API call — returns the URL string)
  getAudioStreamUrl: (contentId, lang = "en") =>
    `${API_ORIGIN}/api/content/${contentId}/audio?lang=${lang}`,
};

// ==================== PLACES/MARKERS API ====================
// Legacy API for backwards compatibility


// ==================== OTHER APIS ====================

export const ttsApi = {
  synthesize: (text, voice = "en-US-Neural2-C", languageCode = "en-US") =>
    api.post("/tts/synthesize", { text, voice, language_code: languageCode }),
  getVoices: () => api.get("/tts/voices"),
};

export const userApi = {
  getReferral: () => api.get("/user/referral"),
  getHistory: () => api.get("/user/history"),
  getStats: () => api.get("/user/stats"),
  trackShare: (placeId, language = "en") =>
    api.post("/user/track-share", { place_id: placeId, language }),
  redeemShare: (token) => api.post(`/user/shares/${encodeURIComponent(token)}/redeem`),
  ratePlace: (placeId, rating) => api.put(`/user/rate/${placeId}`, { rating }),
  getRating: (placeId) => api.get(`/user/rating/${placeId}`),
  getPurchasedLanguages: (placeId) => api.get(`/user/purchased-languages/${placeId}`),
  updatePreferences: (preferences) => api.put("/user/preferences", preferences),
  getCredits: () => api.get("/user/credits"),
  updateProfile: (data) => api.put("/user/profile", data),
  changePassword: (data) => api.put("/user/password", data),
  uploadAvatar: (blob) => {
    const form = new FormData();
    form.append("file", blob, "avatar.jpg");
    return api.post("/user/avatar", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // Folders
  getFolders: () => api.get("/user/folders"),
  createFolder: (name) => api.post("/user/folders", { name }),
  renameFolder: (folderId, name) => api.put(`/user/folders/${folderId}`, { name }),
  deleteFolder: (folderId) => api.delete(`/user/folders/${folderId}`),
  addPlaceToFolder: (folderId, placeId) => api.post(`/user/folders/${folderId}/add-place`, { place_id: placeId }),
  removePlaceFromFolder: (folderId, placeId) => api.delete(`/user/folders/${folderId}/places/${placeId}`),
  reorderFolders: (folderIds) => api.put("/user/folders/reorder", { folder_ids: folderIds }),
  // Tour Plans
  getTourPlans: () => api.get("/user/tour-plans"),
  saveTourPlan: (data) => api.post("/user/tour-plans", data),
  deleteTourPlan: (planId) => api.delete(`/user/tour-plans/${planId}`),
  bundlePurchase: (data) => api.post("/user/tour-plans/bundle-purchase", data),
};

export const paymentsApi = {
  getPackages: () => api.get("/payments/packages"),
  checkoutCredits: (data) => api.post("/payments/checkout/credits", data),
  checkoutSubscription: (data) => api.post("/payments/checkout/subscription", data),
  cancelSubscription: () => api.post("/payments/cancel-subscription"),
  getSubscriptionStatus: () => api.get("/payments/subscription-status"),
  verifyPaymentIntent: (data) => api.post("/payments/payment-intents/verify", data),
  getCheckoutStatus: (sessionId) => 
    api.get(`/payments/checkout/status/${sessionId}`),
  createSetupIntent: () => api.post("/payments/setup-intent"),
  getMethods: () => api.get("/payments/methods"),
  removeMethod: (id) => api.delete(`/payments/methods/${id}`),
};

export default api;
